'use client'

import React, { createContext, useContext, useState } from 'react'
import { User } from '@/lib/types/user-types'
import { AllFieldKeys, FieldValueType } from '@/lib/types/user-types'
import { updateUser } from '@/lib/supabase/queries/user-supabase-query'
import { isUserAdmin } from '@/lib/user/utils'

// --- Context Type Definition ---
type UserContextValue = {
    setUserContextValue: (field: AllFieldKeys, value: FieldValueType<AllFieldKeys>, updateDB: boolean) => Promise<void>
    clearUserContext: () => void
} & (
        | {
            isUserAuthenticated: true
            isAdmin: boolean
            userContextValue: User
        }
        | {
            isUserAuthenticated: false
            isAdmin: boolean
            userContextValue: null
        }
    )

// --- Context Creation ---
const UserContext = createContext<UserContextValue | undefined>(undefined)

// --- Provider Implementation ---
export function UserContextProvider({
    children,
    initialUser,
}: {
    children: React.ReactNode,
    initialUser: User | null,
}) {
    const [userContextValue, setUserState] = useState<User | null>(initialUser)

    // Enhanced setUserContextValue with database persistence for preferences
    const setUserContextValue = async (
        field: AllFieldKeys,
        value: FieldValueType<AllFieldKeys>,
        updateDB: boolean = true
    ) => {
        // User must be authenticated for field updates
        if (!userContextValue) {
            console.warn('Cannot update field when user is not authenticated')
            return
        }
    

        let updatedUser: User
        if (field in userContextValue.settings) {
            // Update preference field locally first
            updatedUser = {
                ...userContextValue,
                settings: {
                    ...userContextValue.settings,
                    [field]: value
                }
            }
        } else {
            updatedUser = {
                ...userContextValue,
                [field]: value
            }
        }

        setUserState(updatedUser)
        if (updateDB) {
            updateUser(updatedUser)
        }
    }

    // Function to clear the user context (for sign out)
    const clearUserContext = () => {
        setUserState(null)
    }

    // Prepare the context value based on authentication status
    const contextValue: UserContextValue = userContextValue
        ? {
            isUserAuthenticated: true,
            isAdmin: isUserAdmin(userContextValue),
            userContextValue,
            setUserContextValue,
            clearUserContext,
        }
        : {
            isUserAuthenticated: false,
            isAdmin: false,
            userContextValue: null,
            setUserContextValue,
            clearUserContext,
        }

    return (
        <UserContext.Provider value={contextValue}>
            {children}
        </UserContext.Provider>
    )
}

// --- Hook to use the context ---
export function useUserContext() {
    const context = useContext(UserContext)
    if (!context) {
        throw new Error('useUserContext must be used within a UserContextProvider')
    }
    return context
} 
